import './i18n/autoTranslate';
import React, { useState, useEffect } from 'react';
import CountUp from '../components/ui/CountUp';
import {
  Check, Shield, Award, Calendar, AlertCircle, RefreshCw,
  Zap, ArrowRight, Star, Users,
  MessageSquare
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import AppBackground from '../components/ui/AppBackground.tsx';

import GlobeComponent from '../components/lightswind/globe';
import SEO from './SEO';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

/* ----------------------------- Animations ----------------------------- */
const SectionWrapper = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    viewport={{ once: true }}
    className={className}
  >
    {children}
  </motion.div>
);

const GlassCard = ({ children, className = "", hover = true }) => (
  <motion.div
    whileHover={hover ? { y: -8, transition: { duration: 0.3 } } : {}}
    className={`
      backdrop-blur-xl
      bg-white/5
      border border-white/10
      rounded-3xl
      shadow-[0_0_40px_rgba(108,99,255,0.08)]
      ${hover ? 'hover:bg-white/8 hover:border-white/20 hover:shadow-[0_0_60px_rgba(108,99,255,0.15)]' : ''}
      transition-all duration-300
      ${className}
    `}
  >
    <img
      src={"/assets/images/Gemini_Generated_6_dlmovh.png"}
      className="absolute -inset-0 object-cover w-full -z-10 h-full rounded-3xl opacity-[0.09]"
      alt=""
    />
    {children}
  </motion.div>
);

const StaggerContainer = ({ children, className = "" }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.1 }}
    variants={{
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1,
          delayChildren: 0.2
        }
      }
    }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ----------------------------- CTA (Upgrade moved to Billing Center) ----------------------------- */
const PlanCTA = ({ plan, userToken, userPlan, navigate }) => {
  const isCurrent = userPlan === plan.plan_type;

  if (isCurrent) {
    return (
      <div className="space-y-3">
        <div className="text-center py-3">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-900/30 text-green-300 rounded-lg">
            <Check className="w-4 h-4" />
            <span className="font-medium">Current Plan</span>
          </div>
        </div>
        <button
          onClick={() => navigate('/my-subscription')}
          className="w-full py-3 px-6 rounded-xl bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition-all duration-300"
        >
          Manage Subscription
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <button
        onClick={() => {
          if (!userToken) {
            // keep pricing public, checkout happens in billing center after login
            window.location.href = '/login?redirect=/plans';
            return;
          }
          navigate('/plans');
        }}
        className={`
          w-full py-4 px-6 rounded-2xl font-semibold text-base transition-all duration-300
          ${plan.is_popular
            ? 'bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-black hover:shadow-[0_0_50px_rgba(250,204,21,0.25)]'
            : 'bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white hover:shadow-[0_0_50px_rgba(108,99,255,0.35)]'}
        `}
      >
        {userToken ? 'Upgrade in Billing Center' : 'Login to Upgrade'}
      </button>
      <p className="mt-3 text-center text-xs text-gray-500">
        Secure checkout + invoices + usage tracking inside your Billing Center.
      </p>
    </div>
  );
};

/* ----------------------------- Main Pricing Component ----------------------------- */
function Pricing() {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userToken, setUserToken] = useState(null);
  const [userPlan, setUserPlan] = useState(null);

  // Fallback data (PayPal IDs kept but checkout moved to Billing Center)
  const fallbackPlans = [
    {
      id: 1,
      plan_type: 'starter',
      name: 'Starter',
      description: 'Perfect for occasional practice and getting started',
      monthly_price: 19.99,
      annual_price: 191.88,
      annual_discount_percentage: 20.0,
      paypal_plan_id: 'P-6BK00171S8189104BNHXAUPA',
      features: [
        '20 AI-powered mock interviews/month',
        'Basic AI feedback & structure analysis',
        'Standard question bank (200+ questions)',
        'Performance dashboard & progress tracking',
        'Email support within 48 hours',
        'General interview scenarios'
      ],
      interviews_per_month: 20,
      question_bank_size: 200,
      is_popular: false,
      display_order: 0,
      is_active: true
    },
    {
      id: 2,
      plan_type: 'professional',
      name: 'Professional',
      description: 'Ideal for serious job seekers and career advancement',
      monthly_price: 49.99,
      annual_price: 479.88,
      annual_discount_percentage: 20.0,
      paypal_plan_id: 'P-78W42235KU403351BNHXAXXI',
      features: [
        '100 AI-powered mock interviews/month',
        'Advanced AI analysis with multi-modal feedback',
        'Role-specific question banks (500+ questions)',
        'Resume-to-interview personalized scenarios',
        'Detailed analytics & performance trends',
        'Video feedback library & recording downloads',
        'Priority email support (24h response)',
        'Customizable interview environments',
        'Progress reports & improvement plans'
      ],
      interviews_per_month: 100,
      question_bank_size: 500,
      is_popular: true,
      display_order: 1,
      is_active: true
    },
    {
      id: 3,
      plan_type: 'executive',
      name: 'Executive',
      description: 'For leadership roles and comprehensive preparation',
      monthly_price: 99.99,
      annual_price: 959.88,
      annual_discount_percentage: 20.0,
      paypal_plan_id: 'P-7PE6932875202884SNHXAYLI',
      features: [
        '300 AI-powered mock interviews/month',
        'Premium AI analysis with executive feedback',
        'Executive-level scenarios (C-suite, board meetings)',
        '1-on-1 monthly coaching sessions (30 min)',
        'Custom question creation & scenario building',
        'Advanced AR interview simulations',
        'Comprehensive analytics dashboard',
        'Phone & priority video support',
        'Team progress monitoring',
        'All Professional features included'
      ],
      interviews_per_month: 300,
      question_bank_size: 1000,
      is_popular: false,
      display_order: 2,
      is_active: true
    }
  ];

  useEffect(() => {
    fetchPlans();
    checkUserAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/subscriptions/plans`);
      if (!response.ok) throw new Error(`API Error: ${response.status}`);

      const data = await response.json();

      if (data.plans && Array.isArray(data.plans)) {
        const transformedPlans = data.plans
          .map(plan => ({
            ...plan,
            features: typeof plan.features === 'string'
              ? JSON.parse(plan.features)
              : plan.features || [],
          }))
          .filter(plan => plan.is_active)
          .sort((a, b) => a.display_order - b.display_order);

        setPlans(transformedPlans);
      } else {
        throw new Error('Invalid data format');
      }
    } catch (err) {
      console.warn('Using fallback data:', err.message);
      setError('Using cached pricing data. Real-time updates unavailable.');
      setPlans(fallbackPlans);
    } finally {
      setLoading(false);
    }
  };

  const checkUserAuth = () => {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (token) {
      setUserToken(token);
      fetchUserPlan(token);
    }
  };

  const fetchUserPlan = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/my-subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUserPlan(userData.plan_name || userData.plan);
      }
    } catch (error) {
      console.error('Failed to fetch user plan:', error);
    }
  };

  const getPlanPrice = (plan) => {
    if (isAnnual) {
      return plan.annual_price || (plan.monthly_price * 12 * (1 - (plan.annual_discount_percentage || 20) / 100)).toFixed(2);
    }
    return plan.monthly_price;
  };

  if (loading) {
    return (
      <div className="bg-[#07070A] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#6C63FF] animate-spin mx-auto mb-4" />
          <p className="text-gray-300">Loading premium plans...</p>
        </div>
      </div>
    );
  }

  return (
    <AppBackground>
      {/* Background Globe */}
      <div className='w-full h-full opacity-[0.2] relative'>
        <SEO
          title="InterviewGenieAI Pricing — AI Interview Practice Plans"
          description="Choose the InterviewGenieAI plan that fits your interview preparation: free practice, advanced AI interviews, resume feedback, analytics, and progress tracking."
          canonical="https://interviewgenieai.com/pricing"
        />
        <GlobeComponent
          size='full'
          theta={0.2}
          dark={1}
          scale={1.1}
          diffuse={1.5}
          baseColor="#1a1a1a"
          markerColor="#6C63FF"
          glowColor="#444444"
          className="absolute"
        />
      </div>

      {/* Background Elements */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#6C63FF]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#8B7CFF]/5 rounded-full blur-3xl" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-8"
            >
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                Premium Interview Preparation
              </span>
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8"
            >
              <span className="bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">
                Premium Plans for
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                Every Career Stage
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8"
            >
              Choose the perfect plan to transform your interview skills with AI-powered precision.
              Upgrade securely in your Billing Center with invoices, payments, and usage tracking.
            </motion.p>

            {/* User Plan Status */}
            {userPlan && userPlan !== 'free' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mb-10"
              >
                <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700/50 rounded-2xl">
                  <Check className="w-5 h-5 text-green-400" />
                  <span className="text-green-300 font-medium">
                    You are currently on the <span className="font-bold text-white">{userPlan}</span> plan
                  </span>
                  <button
                    onClick={() => navigate('/my-subscription')}
                    className="ml-2 text-sm px-3 py-1 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition"
                  >
                    Billing Center
                  </button>
                </div>
              </motion.div>
            )}

            {/* Billing Toggle */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="inline-flex items-center bg-white/5 border border-white/10 rounded-2xl p-1 mb-16"
            >
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-8 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                  !isAnnual
                    ? 'bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] shadow-lg shadow-purple-500/25'
                    : 'hover:bg-white/5'
                }`}
              >
                Monthly Billing
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-8 py-3 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  isAnnual
                    ? 'bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] shadow-lg shadow-purple-500/25'
                    : 'hover:bg-white/5'
                }`}
              >
                Annual Billing
                <span className="px-2 py-1 text-xs bg-gradient-to-r from-green-500 to-emerald-500 rounded-full">
                  Save 20%
                </span>
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {error && (
            <SectionWrapper className="mb-8">
              <div className="max-w-2xl mx-auto p-4 rounded-2xl bg-yellow-900/20 border border-yellow-700/50 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  <p className="text-yellow-200 text-sm">{error}</p>
                </div>
              </div>
            </SectionWrapper>
          )}

          {/* Plans Grid */}
          <StaggerContainer className="grid md:grid-cols-3 gap-8 mb-20">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                {plan.is_popular && (
                  <div className="absolute -top-4 left-1/2 transform w-2/3 text-center -translate-x-1/2 z-20">
                    <div className="px-6 py-2 rounded-full bg-gradient-to-r from-[#6C63FF] via-[#8B7CFF] to-[#6C63FF] text-white text-sm font-bold shadow-lg shadow-purple-500/30">
                      MOST POPULAR
                    </div>
                  </div>
                )}

                {userPlan === plan.plan_type && (
                  <div className="absolute -top-4 left-0 right-0 mx-auto w-fit z-10">
                    <div className="px-4 py-1 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-bold shadow-lg shadow-green-500/30">
                      YOUR CURRENT PLAN
                    </div>
                  </div>
                )}

                <GlassCard
                  className={`p-8 h-full ${plan.is_popular ? 'border-2 border-[#6C63FF]/50' : ''} ${
                    userPlan === plan.plan_type ? 'ring-2 ring-green-500/50' : ''
                  }`}
                >
                  <div className="flex flex-col h-full">
                    {/* Plan Header */}
                    <div className="mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                        {plan.is_popular && <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />}
                      </div>
                      <p className="text-gray-400 mb-6">{plan.description}</p>

                      {/* Price */}
                      <div className="mb-4">
                        <div className="flex items-baseline">
                          <span className="text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
                            ${getPlanPrice(plan)}
                          </span>
                          <span className="text-gray-400 ml-2">/month</span>
                        </div>
                        {isAnnual && plan.monthly_price > 0 && (
                          <p className="text-sm text-gray-500 mt-1">
                            Billed annually (${(getPlanPrice(plan) * 12).toFixed(2)}/year)
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-8 flex-grow">
                      <div className="space-y-4">
                        {plan.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#6C63FF]/20 to-[#8B7CFF]/20 border border-[#6C63FF]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-3 h-3 text-[#6C63FF]" />
                            </div>
                            <span className="text-gray-300 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-8 p-4 rounded-2xl bg-white/5 border border-white/10">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white mb-1">
                          {plan.interviews_per_month === 999 ? '∞' : plan.interviews_per_month}
                        </div>
                        <div className="text-xs text-gray-400">Interviews/Month</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white mb-1">{plan.question_bank_size}+</div>
                        <div className="text-xs text-gray-400">Questions</div>
                      </div>
                    </div>

                    {/* CTA */}
                    <PlanCTA plan={plan} userToken={userToken} userPlan={userPlan} navigate={navigate} />
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </StaggerContainer>

          {/* Guarantees */}
          <SectionWrapper className="text-center">
            <div className="inline-grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {[
                {
                  icon: Shield,
                  title: '7-Day Free Trial',
                  description: 'Try all features risk-free, no credit card required'
                },
                {
                  icon: Award,
                  title: '40% Student Discount',
                  description: 'Special pricing for students and recent graduates'
                },
                {
                  icon: Calendar,
                  title: 'Cancel Anytime',
                  description: 'No contracts, cancel anytime with one click'
                }
              ].map((item, index) => (
                <GlassCard key={index} className="p-6" hover={false}>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-2xl border border-white/20 bg-gradient-to-br from-white/5 to-white/2 flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-[#6C63FF]" />
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2">{item.title}</h4>
                    <p className="text-sm text-gray-400">{item.description}</p>
                  </div>
                </GlassCard>
              ))}
            </div>
          </SectionWrapper>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-[#6C63FF]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <SectionWrapper>
            <GlassCard className="p-12">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 mb-6">
                    <span className="px-3 py-1 rounded-full bg-gradient-to-r from-teal-500/20 to-emerald-500/20 text-teal-300 text-sm font-medium border border-teal-500/30">
                      COMING SOON
                    </span>
                    <span className="text-sm text-gray-400">Enterprise Solutions</span>
                  </div>

                  <h2 className="text-4xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
                    Custom Solutions for Teams & Organizations
                  </h2>

                  <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                    Equip your entire team with the best interview preparation tools.
                    Custom enterprise plans include advanced features tailored for organizations.
                  </p>

                  <div className="space-y-4 mb-8">
                    {[
                      'Custom branding and white-label solutions',
                      'LMS/SCORM integration for seamless training',
                      'Advanced analytics dashboard with team insights',
                      'API access & webhooks for custom workflows',
                      'Dedicated account manager & priority support',
                      'Bulk user management & admin controls'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-cyan-400" />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate('/contact')}
                    className="px-8 py-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold hover:shadow-[0_0_50px_rgba(34,211,238,0.3)] transition-all duration-300"
                  >
                    <MessageSquare className="inline-block mr-2 w-5 h-5" />
                    Schedule Enterprise Demo
                  </motion.button>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  {[
                    { price: '$199', label: 'Small Teams', users: '5-20 users' },
                    { price: '$499', label: 'Medium Teams', users: '21-100 users' },
                    { price: 'Custom', label: 'Large Teams', users: '100+ users' },
                    { price: '$2,000', label: 'Universities', users: 'Per semester' }
                  ].map((item, index) => (
                    <GlassCard key={index} className="p-6 text-center">
                      <div className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-2">
                        {item.price}
                      </div>
                      <div className="text-lg font-semibold text-white mb-1">{item.label}</div>
                      <div className="text-sm text-gray-400">{item.users}</div>
                    </GlassCard>
                  ))}
                </div>
              </div>
            </GlassCard>
          </SectionWrapper>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <SectionWrapper>
            <GlassCard className="p-12 text-center relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#6C63FF]/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#8B7CFF]/10 rounded-full blur-3xl" />

              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
                  Ready to Master Your Interviews?
                </h2>
                <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                  Join thousands of candidates improving with AI-powered precision.
                </p>

                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  {!userToken ? (
                    <>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigate('/sign-up')}
                        className="px-10 py-5 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold text-lg hover:shadow-[0_0_60px_rgba(108,99,255,0.5)] transition-all duration-300 flex items-center justify-center gap-3"
                      >
                        <Zap className="w-5 h-5" />
                        Start Free Trial
                        <ArrowRight className="w-5 h-5" />
                      </motion.button>

                      <button
                        onClick={() => (window.location.href = '/login?redirect=/plans')}
                        className="px-10 py-5 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-lg hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-3"
                      >
                        <Users className="w-5 h-5" />
                        Login to Upgrade
                      </button>
                    </>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/plans')}
                      className="px-10 py-5 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold text-lg hover:shadow-[0_0_60px_rgba(108,99,255,0.5)] transition-all duration-300 flex items-center justify-center gap-3"
                    >
                      <Zap className="w-5 h-5" />
                      Upgrade in Billing Center
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  )}
                </div>

                <p className="text-sm text-gray-500 mt-8 font-medium tracking-wider">
                  Secure checkout • Invoices & receipts • Usage analytics • Cancel anytime
                </p>
              </div>
            </GlassCard>
          </SectionWrapper>
        </div>
      </section>
    </AppBackground>
  );
}

export default Pricing;
